import { z } from 'zod';
import { checkForMissingReferences } from '../../FormulaBuilder/formulaUtils';

import { CampaignQuestion, QuestionType } from '../types';

const baseQuestionSchema = z.object({
    id: z.string().uuid(),
    text: z.string().trim().min(1, { message: 'Question text is required' }),
    title: z.string().optional(),
});

const textQuestionSchema = baseQuestionSchema.extend({
    type: z.literal(QuestionType.TEXT),
});

const booleanQuestionSchema = baseQuestionSchema.extend({
    type: z.literal(QuestionType.BOOLEAN),
});

const numericQuestionSchema = baseQuestionSchema.extend({
    type: z.literal(QuestionType.NUMERIC),
    minValue: z.number(),
    maxValue: z.number(),
    useRatingScale: z.boolean().optional(),
});

const multiSelectQuestionSchema = baseQuestionSchema.extend({
    type: z.literal(QuestionType.MULTISELECT),
    options: z.array(z.string().min(1, { message: 'Option cannot be empty' })).min(2, { message: 'At least two option are required' }),
});

const formulaQuestionSchema = baseQuestionSchema
    .extend({
        type: z.literal(QuestionType.FORMULA),
        formula: z.string().min(1, { message: 'Formula is required' }),
        isQualityScore: z.boolean().optional(),
    })
    .omit({ text: true })
    .extend({
        text: z.string().optional(),
    });

const questionSchema = z.discriminatedUnion('type', [
    textQuestionSchema,
    booleanQuestionSchema,
    numericQuestionSchema,
    multiSelectQuestionSchema,
    formulaQuestionSchema,
]);

export const formSchema = z
    .object({
        questions: z.array(questionSchema),
    })
    .superRefine((data, ctx) => {
        // Validate that only one formula has isQualityScore (quality score)
        const qualityFormulas = data.questions.filter(q => q.type === QuestionType.FORMULA && q.isQualityScore === true);

        if (qualityFormulas.length > 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Only one formula can be marked as quality score',
                path: ['questions'],
            });
        }

        // Validate that formulas don't contain missing questions
        data.questions.forEach((question, index) => {
            if (question.type === QuestionType.FORMULA) {
                const validation = checkForMissingReferences(question.formula, data.questions as CampaignQuestion[]);

                if (validation.hasMissingReferences) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Formula contains missing question${validation.missingQuestions.length > 1 ? 's' : ''}`,
                        path: ['questions', index, 'formula'],
                    });
                }
            }
        });
    });

export type FormValues = z.infer<typeof formSchema>;
